'use client';

import { useRef, useEffect, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';

type Props = {
  children: ReactNode;
  className?: string;
  delay?: number;   // ms
  y?: number;       // px lift, default 24
  amount?: number;  // kept for API compat, unused
};

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function ScrollReveal({ children, className, delay = 0, y = 24 }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;

    // Don't hide elements that are already in the viewport on page load.
    const { top, bottom } = el.getBoundingClientRect();
    if (top < window.innerHeight && bottom > 0) return;

    el.style.opacity = '0';
    el.style.transform = `translateY(${y}px)`;
    el.style.transition = `opacity 0.55s ${EASE} ${delay}ms, transform 0.55s ${EASE} ${delay}ms`;
    el.style.willChange = 'opacity, transform';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
        const done = () => {
          el.style.willChange = 'auto';
          el.removeEventListener('transitionend', done);
        };
        el.addEventListener('transitionend', done);
        observer.disconnect();
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [reduced, delay, y]);

  return <div ref={ref} className={className}>{children}</div>;
}
